import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { Card, ModuleTile } from '@ifs-gcp/components';
import type { AuditCreation, ITemplateNode } from '@ifs-gcp/core/core';
import {
  isAssessmentsListWidgetNode,
  isCardNode,
  isContainerNode,
  isModuleTile,
  isTextNode,
} from '@ifs-gcp/core/helpers';
import type { BaseTemplateElement } from '@ifs-gcp/core/models';

import { getCardProps } from '@/utils/card';
import { getTranslation } from '@/utils/translations';

import { SelfAssessmentsList } from '../self-assessments-list';
import styles from './dashboard-element.module.scss';

interface DashboardElementProps {
  element: ITemplateNode<BaseTemplateElement>;
  supplierId: string;
  domain: AuditCreation;
  handleOpenModal: () => void;
}

type ElementProps = Omit<DashboardElementProps, 'element'>;

const renderTextElement = (type: string, translatedText: string) => {
  switch (type) {
    case 'sub-title':
      return <h6 className={styles.subtitle}>{translatedText}</h6>;
    case 'paragraph':
      return <p className={styles.description}>{translatedText}</p>;
    default:
      return null;
  }
};

const renderCardChildren = (
  cardBody: ITemplateNode<BaseTemplateElement>[],
  elementProps: ElementProps,
) => {
  return cardBody?.map((cardElement, index) => (
    <DashboardElement
      key={`${cardElement.type}-${index}`}
      element={cardElement}
      {...elementProps}
    />
  ));
};

export const DashboardElement = observer((props: DashboardElementProps) => {
  const {
    i18n: { language },
  } = useTranslation();

  const { element, domain, handleOpenModal, supplierId } = props;

  const elementProps: ElementProps = {
    handleOpenModal,
    supplierId,
    domain,
  };

  const handleSelectModule = (context: string[]) => {
    /**
     * We remove modules which have been added prior but not started (modal canceled)
     */
    Array.from(domain.modulesHandler.modules.keys()).forEach((moduleId) =>
      domain.modulesHandler.deleteModule(moduleId),
    );
    /**
     * Since we do not have any start audit button like in AXPX desktop
     * clicking a module tile will open the modal directly until we add more modules
     */
    context.forEach((moduleContext) => domain.modulesHandler.addModule(moduleContext));

    handleOpenModal();
  };

  if (!element) return null;

  if (isContainerNode(element)) {
    const {
      props: { maxWidth, align },
      children,
    } = element;

    return (
      <div className={styles.container} style={{ maxWidth, textAlign: align }}>
        {children?.map((containerElement, index) => (
          <DashboardElement
            key={`${containerElement.type}-${index}`}
            element={containerElement}
            {...elementProps}
          />
        ))}
      </div>
    );
  }

  if (isTextNode(element)) {
    const {
      type,
      props: { text },
    } = element;
    const translatedText = getTranslation(text, language);

    return renderTextElement(type, translatedText);
  }

  if (isModuleTile(element)) {
    const { props: moduleTileProps } = element;

    const logoPath =
      moduleTileProps.kind === 'logo' ? `/assets/definitions/${moduleTileProps.logoPath}` : '';

    return (
      <div className={styles.moduleTile}>
        <ModuleTile
          {...moduleTileProps}
          logoPath={logoPath}
          selected={moduleTileProps.context.some((id) => domain.modulesHandler.modules.has(id))}
          onClick={() => handleSelectModule(moduleTileProps.context)}
          versionText={getTranslation(moduleTileProps.versionText, language)}
        />
      </div>
    );
  }

  if (isCardNode(element)) {
    const { cardTitle, cardBody } = getCardProps(element.children);

    if (!cardBody) return null;

    const cardChildren = renderCardChildren(cardBody, elementProps);

    return <Card title={getTranslation(cardTitle, language)}>{cardChildren}</Card>;
  }

  if (isAssessmentsListWidgetNode(element)) {
    return <SelfAssessmentsList supplierId={supplierId} labels={element.props.labels} />;
  }

  return null;
});

DashboardElement.displayName = 'DashboardElement';
